import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddProductionTemplateAutocomplete from 'components/shared/add-production-template-autocomplete/add-production-template-autocomplete.component';
import AddStepAutocomplete, {
    StepOption,
} from 'components/shared/add-step-autocomplete/add-step-autocomplete.component';
import { useQuery } from '@apollo/client';
import { getProductionPlanningTemplatesTemplate } from 'apollo/templates/organization';
import { getLabActionsTemplate } from 'apollo/templates/action';
import { getActionBasedActionListTemplate } from 'apollo/templates/client-directory';
import { workFlowTemplate } from 'apollo/templates/workFlow';
import { WorkFlow } from 'models/workflow-step';
import { ProductionPlanningTemplate } from 'models/organization';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { ProductionPlanningStep } from 'models/production-planning';
import { useDepartments } from 'context/departments-context';
import {
    ConstructionForTemplateMatching,
    ActionBasedAction,
    collectMatchingTemplateIds,
    buildTemplateQuantityMap,
    buildStepsFromTemplate,
} from './production-scheduling.utils';

export type { ConstructionForTemplateMatching };

type SchedulingActionsProps = {
    steps: ProductionPlanningStep[];
    onStepsChange: (steps: ProductionPlanningStep[]) => void;
    constructions?: ConstructionForTemplateMatching[];
    sampleType?: string;
    onAddStep: (step: StepOption) => void;
    disableAutoTemplate?: boolean;
    clinicId?: string;
};

const SchedulingActions: FC<SchedulingActionsProps> = ({
    steps,
    onStepsChange,
    constructions,
    sampleType,
    onAddStep,
    disableAutoTemplate,
    clinicId,
}) => {
    const { t: translate } = useTranslation(['orders']);
    const { data: templatesData } = useQuery(getProductionPlanningTemplatesTemplate);
    const { data: workFlowData } = useQuery(workFlowTemplate);
    const { departments, getDepartmentNameForStep, getDepartmentIdForStep } = useDepartments();

    const usedStepIds = useMemo(() => new Set(steps.map((s) => s.stepId)), [steps]);
    const workflowSteps = (workFlowData?.workFlow as WorkFlow)?.steps || [];
    const stepOptions: StepOption[] = useMemo(
        () =>
            workflowSteps
                .filter((s) => !usedStepIds.has(s._id || ''))
                .map((s) => ({
                    id: s._id || '',
                    name: s.name,
                    departmentName: getDepartmentNameForStep(s._id || ''),
                })),
        [workflowSteps, departments, usedStepIds, getDepartmentNameForStep],
    );

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingTemplate, setPendingTemplate] = useState<ProductionPlanningTemplate | null>(null);

    const { data: actionsData } = useQuery(getLabActionsTemplate);
    const { data: actionBasedData } = useQuery(getActionBasedActionListTemplate, {
        variables: { clinicId },
        skip: !clinicId,
    });
    const productionTemplates: ProductionPlanningTemplate[] = templatesData?.productionPlanningTemplates ?? [];

    const labActions = actionsData?.labActions || [];
    const actionBasedActions: ActionBasedAction[] = actionBasedData?.actionBasedActionList || [];

    const automaticTemplates = useMemo(() => {
        if ((!labActions.length && !actionBasedActions.length) || !productionTemplates.length || !constructions)
            return [];

        const orderActionIds = new Set(constructions.flatMap((c) => c.actions.map((a) => a.actionId)));
        const templateIds = collectMatchingTemplateIds(orderActionIds, labActions, actionBasedActions, sampleType);

        return productionTemplates.filter((t) => t._id && templateIds.has(t._id));
    }, [actionsData, actionBasedData, productionTemplates, constructions, sampleType]);

    const hasAutoApplied = useRef(false);
    const prevSampleTypeRef = useRef(sampleType);

    useEffect(() => {
        if (prevSampleTypeRef.current !== sampleType) {
            prevSampleTypeRef.current = sampleType;
            hasAutoApplied.current = false;
        }
    }, [sampleType]);

    const applyTemplates = (templates: ProductionPlanningTemplate[]) => {
        const existingStepIds = new Set(steps.map((s) => s.stepId));
        const alreadyAdded = new Set<string | undefined>();

        const templateQuantityMap =
            constructions && (labActions.length > 0 || actionBasedActions.length > 0)
                ? buildTemplateQuantityMap(constructions, labActions, actionBasedActions, sampleType)
                : new Map<string, number>();

        const newSteps: ProductionPlanningStep[] = [];
        templates.forEach((template) => {
            const quantity = template._id ? templateQuantityMap.get(template._id) || 1 : 1;
            const templateSteps = buildStepsFromTemplate(
                template,
                quantity,
                constructions,
                getDepartmentIdForStep,
                existingStepIds,
                alreadyAdded,
            );
            newSteps.push(...templateSteps);
        });

        onStepsChange([...steps, ...newSteps]);
    };

    useEffect(() => {
        if (!disableAutoTemplate && automaticTemplates.length > 0 && !hasAutoApplied.current) {
            hasAutoApplied.current = true;
            applyTemplates(automaticTemplates);
        }
    }, [automaticTemplates, disableAutoTemplate]);

    const handleTemplateSelect = (template: ProductionPlanningTemplate) => {
        if (steps.length > 0) {
            setPendingTemplate(template);
            setConfirmOpen(true);
        } else {
            applyTemplates([template]);
        }
    };

    const handleConfirmMerge = () => {
        if (pendingTemplate) {
            applyTemplates([pendingTemplate]);
        }
        setConfirmOpen(false);
        setPendingTemplate(null);
    };

    return (
        <>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={7}>
                    <AddProductionTemplateAutocomplete
                        options={productionTemplates}
                        onTemplateSelected={handleTemplateSelect}
                    />
                </Grid>
                <Grid item xs={3}>
                    <AddStepAutocomplete options={stepOptions} onStepSelected={onAddStep} />
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="flex-end">
                    <Button color="error" onClick={() => onStepsChange([])} disabled={!steps.length}>
                        {translate('orders:CLEAR_PLAN')}
                    </Button>
                </Grid>
            </Grid>

            <ConfirmDialog
                open={confirmOpen}
                handleClose={() => {
                    setConfirmOpen(false);
                    setPendingTemplate(null);
                }}
                handleConfirm={handleConfirmMerge}
                dialogContent={translate('orders:TEMPLATE_MERGE_CONFIRM')}
            />
        </>
    );
};

export default SchedulingActions;
